<template>
  <div
    class="row-center-gap-1 colored-tag justify-center min-w-0 max-w-full border"
    v-bind:class="[color ? `tag-variant-${variant}` : '', sizeClass, shapeClass]"
    v-bind:style="tagStyle"
    :role="tagRole"
    :aria-label="ariaLabel"
  >
    <div v-if="tagIcon" v-bind:class="`tag-icon shrink-0 ti ti-${tagIcon}`"></div>
    <div v-if="tagText" class="tag-text truncate" :class="{ 'font-semibold': secondaryText }">
      {{ tagText }}
      <span v-if="secondaryText" class="tag-secondary ml-0.5 text-xxxs font-normal opacity-60">
        {{ secondaryText }}
      </span>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from "vue";
import type { PropType } from "vue";
import type { ComponentSizeType } from "@/types/componentType";
import type { ComponentColorType, TagVariant } from "@/types/componentType";
import { BADGE_SIZE } from "@/constants/sizing";

export default defineComponent({
  name: "ColoredTag",
  props: {
    tagText: { type: String },
    secondaryText: { type: String, default: "" },
    tagIcon: { type: String },
    variant: { type: String as PropType<TagVariant>, default: "filled" },
    size: { type: String as PropType<ComponentSizeType>, default: "small" },
    color: { type: String as PropType<ComponentColorType>, default: "" },
    tagRole: { type: String, default: undefined },
    ariaLabel: { type: String, default: undefined },
    // Opt-in fully-rounded pill shape (SLA/countdown style). Off by default so every
    // existing call site keeps the standard rounded-lg badge.
    isPill: { type: Boolean, default: false },
  },
  setup(props) {
    const sizeClass = computed(
      () => `${BADGE_SIZE[props.size].font} ${BADGE_SIZE[props.size].pad}`,
    );
    const shapeClass = computed(() => (props.isPill ? "rounded-full" : "rounded-lg"));
    const tagStyle = computed(() =>
      props.color
        ? {
            "--tag-color": `var(--color-${props.color})`,
            "--tag-color-bg": `var(--color-${props.color}-bg)`,
            "--tag-color-text": `var(--color-${props.color}-dark)`,
          }
        : {},
    );
    return { sizeClass, shapeClass, tagStyle };
  },
});
</script>

<style lang="scss" scoped>
// scoped-defer: bucket-a-leakage: unmappable decl(s): border
.colored-tag {
  &.tag-variant-filled {
    background: var(--tag-color);
    border-color: var(--tag-color);
    color: var(--color-light);
  }
  &.tag-variant-outline {
    background: transparent;
    border-color: var(--tag-color);
    color: var(--tag-color);
  }
  &.tag-variant-soft {
    background: var(--tag-color-bg, var(--tag-color));
    border-color: transparent;
    color: var(--tag-color-text, var(--tag-color));
  }
}
</style>
